import React, { useEffect, useRef, useState } from 'react';
import { de } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const fmt = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const isoVon = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const alsDatum = (iso) => (iso ? new Date(`${iso}T12:00:00`) : undefined);

// Akzeptiert TT.MM.JJJJ, T.M.JJ, TTMMJJJJ und JJJJ-MM-TT.
export const datumLesen = (t) => {
  const s = (t || '').trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  let j, mo, ta;
  if (m) [, j, mo, ta] = m;
  else if ((m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/))) [, ta, mo, j] = m;
  else if ((m = s.match(/^(\d{2})(\d{2})(\d{4}|\d{2})$/))) [, ta, mo, j] = m;
  else return null;
  if (j.length === 2) j = Number(`20${j}`) > new Date().getFullYear() + 10 ? `19${j}` : `20${j}`;
  const iso = `${j}-${String(mo).padStart(2, '0')}-${String(ta).padStart(2, '0')}`;
  const d = alsDatum(iso);
  return d && isoVon(d) === iso ? iso : null;
};

// Deutsches Datumsfeld: eintippen (TT.MM.JJJJ) oder im Kalender wählen. Wert/onChange wie <input type="date"> (ISO JJJJ-MM-TT).
export const DatumFeld = ({ value, defaultValue, onChange, onBlur, min, max, disabled, className = '', autoFocus, required, title, placeholder = 'TT.MM.JJJJ', jahrAuswahl, ...rest }) => {
  const kontrolliert = value !== undefined;
  const [iso, setIso] = useState(kontrolliert ? value || '' : defaultValue || '');
  const [txt, setTxt] = useState(fmt(iso));
  const [offen, setOffen] = useState(false);
  const [fehler, setFehler] = useState('');
  const ref = useRef(null);
  const wrap = useRef(null);
  useEffect(() => { if (kontrolliert) { setIso(value || ''); setTxt(fmt(value || '')); } }, [value, kontrolliert]);
  const grenze = (neu) => (neu && min && neu < min ? `frühestens ${fmt(min)}` : neu && max && neu > max ? `spätestens ${fmt(max)}` : '');
  const setzen = (neu) => {
    const g = grenze(neu);
    if (g) { setFehler(g); setTxt(fmt(iso)); return false; }
    setFehler(''); setIso(neu); setTxt(fmt(neu));
    if (neu !== iso) onChange?.({ target: { value: neu } });
    return true;
  };
  const verlassen = (e) => {
    const neu = datumLesen(txt);
    if (neu === null) { setFehler('Bitte als TT.MM.JJJJ eingeben'); setTxt(fmt(iso)); } else setzen(neu);
    if (!offen && !wrap.current?.contains(e?.relatedTarget)) onBlur?.();
  };
  const breit = /\bw-full\b/.test(className);
  return (
    <span ref={wrap} className={`relative ${breit ? 'flex w-full' : 'inline-flex'} items-center`} data-datumfeld>
      <input ref={ref} type="text" inputMode="numeric" autoComplete="off" value={txt} placeholder={placeholder} disabled={disabled} autoFocus={autoFocus} required={required} title={fehler || title}
        aria-invalid={!!fehler} onChange={(e) => { setTxt(e.target.value); setFehler(''); e.target.setCustomValidity(''); }} onBlur={verlassen}
        onInvalid={(e) => e.target.setCustomValidity('Bitte ein Datum eingeben (TT.MM.JJJJ)')}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verlassen(); } if (e.key === 'ArrowDown' && e.altKey) setOffen(true); }}
        className={`${className} pr-8 ${breit ? '' : 'w-[8.5rem]'} ${fehler ? '!border-rose-400' : ''}`} {...rest} />
      <Popover open={offen} onOpenChange={(o) => { setOffen(o); if (!o) onBlur?.(); }}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Kalender öffnen" data-tipp="Kalender öffnen" data-testid={rest['data-testid'] ? `${rest['data-testid']}-kalender` : undefined}
            className="absolute right-1 inline-flex items-center justify-center w-6 h-6 rounded text-[#8f6a2b] hover:bg-[#fbf7f0] disabled:opacity-40">
            <CalendarDays className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" locale={de} weekStartsOn={1} {...(jahrAuswahl ? { captionLayout: 'dropdown-buttons', fromYear: 1920, toYear: new Date().getFullYear() + 10 } : {})} selected={alsDatum(iso)} defaultMonth={alsDatum(iso) || alsDatum(min)}
            disabled={[...(min ? [{ before: alsDatum(min) }] : []), ...(max ? [{ after: alsDatum(max) }] : [])]}
            onSelect={(d) => { if (d && setzen(isoVon(d))) setOffen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
      {fehler && <span role="alert" className="absolute left-0 top-full mt-0.5 text-[10.5px] text-rose-700 whitespace-nowrap z-10">{fehler}</span>}
    </span>
  );
};
